import React from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import { push } from 'react-router-redux';
import { updateHistory } from '@eventbrite/react-router-history';
import {
    StatsigProvider as DefaultStatsigProvider,
    StatsigSynchronousProvider,
} from '@eventbrite/statsig';

import { GA_SETTINGS_PROP_TYPE } from '@eventbrite/site-analytics';

import {
    SERVER_ERRORS_SHAPE,
    WAITING_ROOM_SHAPE,
    ENV_SHAPE_PROP_TYPE,
    REFUND_POLICY_CODE_SHAPE,
    SOCIAL_SSO_SETTINGS_SHAPE,
    TEAM_SETTINGS_SHAPE,
    THEME_SETTINGS_PROP,
} from './constants';
import getRoutes from './routes';
import { getStatsigConfigFromState } from './selectors/statsig';
import './app.scss';

export default class CheckoutWidgetApp extends React.Component {
    static propTypes = {
        appName: PropTypes.string.isRequired,
        appVersion: PropTypes.string,

        /**
         * Environment related props
         */
        env: ENV_SHAPE_PROP_TYPE.isRequired,

        /**
         * Passed down to the App level Page component and needed for postMessage to communicate with
         * the organizer site. Required for any site other than /checkout-external
         */
        widgetParentUrl: PropTypes.string,

        /**
         * Waiting room related props
         */
        waitingRoom: WAITING_ROOM_SHAPE.isRequired,

        /**
         * An array of delivery methods
         */
        deliveryMethods: PropTypes.arrayOf(PropTypes.string),

        /**
         * Square API URL. Passed down to the App level Page component and needed for postMessage to communicate with
         * the square.
         */
        squareApiHost: PropTypes.string,

        /**
         * Eventbrite market account on Square. Passed down to the App level Page component and needed for postMessage
         * to communicate with the square.
         */
        squareMarketAccount: PropTypes.string,

        /**
         * Feature Flags
         */
        featureFlags: PropTypes.shape({
            enable3DSecure2: PropTypes.bool,
            enableEBMarketingOptIn: PropTypes.bool,
            enableEBMarketingOptInImplicit: PropTypes.bool,
            enableNonceBraintreePayments: PropTypes.bool,
            enableRefundFeeRetentionPolicy: PropTypes.oneOfType([
                PropTypes.bool,
                PropTypes.number,
            ]),
            enableSquareCreditCardCheckout: PropTypes.bool,
            force3DSecure2VerifyCard: PropTypes.bool,
            launchEmbeddedCheckoutAttendeeLoginOnThirdPartyWebsite:
                PropTypes.bool,
            launchEmbeddedCheckoutRetryOnPlaceError: PropTypes.bool,
            launchSupportMultipleFacebookPixels: PropTypes.bool,
            shouldHideSeriesStartTime: PropTypes.bool,
            disableRecaptcha3: PropTypes.bool,
            enableSyncReactRouterInitialization: PropTypes.bool,
        }),

        /**
         * Grylls configuration information
         */
        grylls: PropTypes.shape({
            trackingData: PropTypes.object,
            options: PropTypes.shape({
                enabled: PropTypes.bool,
                debugging: PropTypes.bool,
            }),
        }),
        /**
         * True if Registration terminology is enabled
         */
        isRegistrationEvent: PropTypes.bool,
        /**
         * True if Event is a Digital Content event
         */
        hasDigitalContent: PropTypes.bool,
        /**
         * Custom URL for viewing a user's tickets after completion of the order
         */
        ticketCallbackUrl: PropTypes.string,
        /**
         * Google Analytics session and user data
         */
        gaSettings: GA_SETTINGS_PROP_TYPE,
        /**
         * Errors sent from the server to be rendered on load.
         * For now, the server will send a max of 1 error at a time.
         */
        errors: SERVER_ERRORS_SHAPE,
        /**
         * Invite token to be able to bypass protected events
         */
        inviteToken: PropTypes.string,
        /**
         * Invite email to be able to prefill the email field
         */
        inviteEmail: PropTypes.string,
        /**
         * Campaign token to be able to bypass invite-only events
         */
        campaignToken: PropTypes.string,
        /**
         * Team token to be able to bypass group registration team selection screens
         */
        teamToken: PropTypes.string,
        /**
         * Team settings to be able to bypass group registration team selection screens
         */
        teamSettings: TEAM_SETTINGS_SHAPE,
        /**
         * Promo code from request to be used to pass down to children of repeating events
         */
        requestPromoCode: PropTypes.string,
        /**
         * IOS profile, customize checkout widget for iOS devices.
         */
        useIosProfile: PropTypes.bool,
        /**
         * Android profile, customize checkout widget for Android devices.
         */
        useAndroidProfile: PropTypes.bool,
        /**
         * Whether the Postal Code should be asked when purchasing
         */
        countryWithPostalCode: PropTypes.bool,
        /**
         * Theme override data for customization
         */
        themeSettings: THEME_SETTINGS_PROP,
        /**
         * Whether we show organizer/organization marketing opt in checkbox
         */
        showOrgMarketingOptIn: PropTypes.bool,
        /**
         * Refund policy code
         */
        refundPolicyCode: REFUND_POLICY_CODE_SHAPE,
        /**
         * Trigger the PayIn flow
         */
        isPayinsFlow: PropTypes.bool,
        /**
         * Maximum amount allowed in payins
         */
        maxAllowedPayin: PropTypes.number,
        /*
         * Amount that will be sent to organizer on next payout.
         */
        refundableBalance: PropTypes.number,
        /*
         * Whether the country is within GDPR regulations.
         */
        isGDPRCountry: PropTypes.bool,
        /*
         * Captcha configuration
         */
        captcha: PropTypes.shape({
            /**
             * Google captcha v2 key
             */
            recaptcha2PublicKey: PropTypes.string,
            /**
             * Google captcha v3 key
             */
            recaptcha3PublicKey: PropTypes.string,
        }),
        /**
         * Configuration for social logins within the login library.
         */
        socialSSOSettings: SOCIAL_SSO_SETTINGS_SHAPE,
        /**
         * Split IO production key
         */
        splitIOProdKey: PropTypes.string,
        statsigInitializeValues: PropTypes.object,
    };

    constructor(props) {
        super(props);

        if (!props.featureFlags.enableSyncReactRouterInitialization) {
            updateHistory(props.history, props.request);
        }
    }

    componentDidMount() {
        if (!this.props.featureFlags.enableSyncReactRouterInitialization) {
            this.props.store.dispatch(push(this.props.initialPath));
        }
    }

    UNSAFE_componentWillReceiveProps({ request }) {
        if (!this.props.featureFlags.enableSyncReactRouterInitialization) {
            updateHistory(this.props.history, request);
        }
    }

    render() {
        const { statsigInitializeValues, store, history } = this.props;

        const statsigUser = getStatsigConfigFromState(store.getState());

        const content = (
            <Provider store={store}>
                <Router history={history} routes={getRoutes()} />
            </Provider>
        );

        return statsigInitializeValues ? (
            <StatsigSynchronousProvider
                initializeValues={statsigInitializeValues}
                user={statsigUser}
            >
                {content}
            </StatsigSynchronousProvider>
        ) : (
            <DefaultStatsigProvider user={statsigUser}>
                {content}
            </DefaultStatsigProvider>
        );
    }
}
